// src/pages/draft_document/DraftDocumentDetail.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../api";
import styles from "./DraftDocumentDetail.module.css";
import IncomeProposalPreview from "../../components/preview/income/IncomeProposalPreview";
import ExpenseResolutionPreview, { buildExpenseResolutionProps } from "../../components/preview/expense/ExpenseResolutionPreview";
import IncomeResolutionPreview, { buildIncomeResolutionProps } from "../../components/preview/income/IncomeResolutionPreview";
import { YONGYEOK_CLAIM_SITE_KEYS, YONGYEOK_SUBMIT_SITE_KEYS } from "../../components/document-type/ServiceGiseongAutoFill";
import { RESOLUTION_DOCUMENT_TYPES } from "../../utils/resolutionDraft";

const CODE_TO_PAY_METHOD = {
  transfer: "계좌이체",
  card: "카드결제",
  cash: "현금결제",
};

const DOC_TYPE_LABEL = {
  income_draft: "수입기안",
  expense_draft: "지출기안",
  income_resolution: "수입결의서",
  expense_resolution: "지출결의서",
  leave_request: "휴가신청서",
};

// skipped(생략) = 결재 단계를 승인 없이 종결한 것(예: 퇴임). 회색 — 승인(초록)과 섞이지 않게.
const RESULT_LABEL = { pending: "대기", approved: "승인", rejected: "반려", skipped: "생략" };
const RESULT_COLOR = { pending: "#aaa", approved: "#27ae60", rejected: "#e74c3c", skipped: "#7f8c8d" };


export default function DraftDocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get("view") === "official" ? "official" : "internal";

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchDoc();
    fetchCurrentUser();
  }, [id]);

  const fetchDoc = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/draft/documents/${id}/`);
      setDoc(data);
    } catch (err) {
      console.error("❌ 기안서 불러오기 실패:", err);
      alert("기안서를 불러오는 데 실패했습니다.");
      navigate("/approval/draftdocument");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const { data } = await api.get("/users/me/");
      setCurrentUser(data);
    } catch (err) {
      console.error("❌ 현재 사용자 정보 불러오기 실패:", err);
    }
  };

  const myApprovalLine = doc?.approval_lines?.find(
    (line) =>
      line.approver_id === currentUser?.id &&
      (line.status === "pending" || line.approval_status === "pending")
  );

  const canApprove = !!myApprovalLine && doc?.status === "pending";

  const handleApprove = async () => {
    if (!window.confirm("이 기안서를 승인하시겠습니까?")) return;
    setActionLoading(true);
    try {
      await api.post(`/draft/documents/${id}/approve/`);
      alert("✅ 승인되었습니다.");
      fetchDoc();
    } catch (err) {
      console.error("❌ 승인 실패:", err);
      alert("승인 처리 중 오류가 발생했습니다.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("반려 사유를 입력해주세요.");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/draft/documents/${id}/reject/`, { reason: rejectReason });
      alert("반려 처리되었습니다.");
      setShowRejectModal(false);
      setRejectReason("");
      fetchDoc();
    } catch (err) {
      console.error("❌ 반려 실패:", err);
      alert("반려 처리 중 오류가 발생했습니다.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.querySelector(".preview-paper");
      if (!element) {
        alert("미리보기 영역을 찾을 수 없습니다.");
        return;
      }

      const { default: html2pdf } = await import("html2pdf.js");
      const opt = {
        margin: 0,
        filename: `${doc.draft_no || "기안서"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: 794,
          height: 1123,
        },
        jsPDF: {
          unit: "px",
          format: [794, 1123],
          orientation: "portrait",
          hotfixes: ["px_scaling"],
        },
      };
      html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("❌ PDF 다운로드 실패:", err);
      alert("PDF 생성 중 오류가 발생했습니다.");
    }
  };

  const getDocStatusBadge = (status) => {
    const map = {
      draft:    { text: "작성중", cls: styles.docDraft },
      pending:  { text: "결재중", cls: styles.docPending },
      approved: { text: "승인",   cls: styles.docApproved },
      rejected: { text: "반려",   cls: styles.docRejected },
    };
    return map[status] || { text: status, cls: styles.docDraft };
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
        <p>불러오는 중...</p>
      </div>
    );
  }

  if (!doc) return null;

  const docStatusBadge = getDocStatusBadge(doc.status);
  const payMethodLabel = CODE_TO_PAY_METHOD[doc.pay_method] || "계좌이체";

  // ✅ 용역 수입기안 청구형(공단 계약)/제출형(기성계 제출) — template_code로 판별, template_data에서 복원
  const isYongyeokClaim = YONGYEOK_CLAIM_SITE_KEYS.includes(doc.template_code);
  const isYongyeokSubmit = YONGYEOK_SUBMIT_SITE_KEYS.includes(doc.template_code);
  const td = doc.template_data || {};

  const formData = {
    recipient: "내부결재",
    title: doc.title || "",
    documentType: doc.document_type || "income_draft",
    businessType: doc.category || "",
    detailType: doc.subcategory || "",
    detailOption: doc.detail_option || "",
    workMonth: doc.work_month || "",
    startDate: doc.work_month || "",
    endDate: doc.work_month || "",
    client: doc.partner_name || "",
    amount: String(doc.amount || ""),
    transactionType: {
      계좌이체: payMethodLabel === "계좌이체",
      카드결제: payMethodLabel === "카드결제",
      현금결제: payMethodLabel === "현금결제",
    },
    manualDate: doc.claim_date || doc.draft_date || "",
    // ✅ 지출결의/수입결의는 자체 채번을 안 하고 원본 기안 draft_no를 재사용하는데, 모델의
    // draft_no unique 제약 때문에 실제 저장값엔 "-R{순번}" 접미가 붙어있다(ExpenseResolutionNew.jsx/
    // IncomeResolutionNew.jsx 저장 로직 참고). 화면엔 이 접미가 보이면 안 되므로
    // template_data.internalDraftNo(원본)를 우선 사용.
    documentNumber: (RESOLUTION_DOCUMENT_TYPES.includes(doc.document_type) ? td.internalDraftNo : null) || doc.draft_no || "",
    content: doc.desc || "",
    attachments: doc.attachment_items || [],
    ...(isYongyeokClaim && {
      tableType: "yongyeok_claim",
      activeView,
      officialDraftNo: td.officialDraftNo || "",
      yongyeokRound: td.yongyeokRound || "",
      yongyeokContractAmount: td.yongyeokContractAmount || "",
      yongyeokPrevAmount: td.yongyeokPrevAmount || "",
      yongyeokPrevCumulative: td.yongyeokPrevCumulative || "",
      yongyeokContractName: td.yongyeokContractName || "",
      yongyeokReferenceLabel: td.yongyeokReferenceLabel || "",
    }),
    ...(isYongyeokSubmit && {
      tableType: "yongyeok_submit",
      activeView,
      officialDraftNo: td.officialDraftNo || "",
      yongyeokSubmitServiceName: td.yongyeokSubmitServiceName || "",
      yongyeokSubmitRecipient: td.yongyeokSubmitRecipient || "",
      yongyeokSubmitHonorific: td.yongyeokSubmitHonorific || "",
      yongyeokSubmitViaLabel: td.yongyeokSubmitViaLabel || "",
    }),
  };

  const approvalLine = (doc.approval_lines || [])
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((line) => ({
      id: line.approver_id,
      name: line.name || line.approver_name || "",
      position: line.position || "",
      status: line.approval_type || "결재",
      approval_type: line.approval_type || "결재",
    }));

  return (
    <div className={styles.page}>

      {/* ── 상단 액션바 ── */}
      <div className={styles.topBar}>
        <div className={styles.topLeft}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            ← 목록으로
          </button>
          <span className={`${styles.docStatusBadge} ${docStatusBadge.cls}`}>
            {docStatusBadge.text}
          </span>
          <span className={styles.docTypeTag}>
            {DOC_TYPE_LABEL[doc.document_type] || doc.document_type}
          </span>
        </div>

        <div className={styles.topActions}>
          {canApprove && (
            <>
              <button
                className={styles.approveBtn}
                onClick={handleApprove}
                disabled={actionLoading}
              >
                ✅ 승인
              </button>
              <button
                className={styles.rejectBtn}
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
              >
                ❌ 반려
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── 결재선 현황 ── */}
      <div style={{ padding: "16px 24px", background: "#fff", borderBottom: "1px solid #e9ecef" }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>결재선 현황</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {(doc.approval_lines || [])
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((line, i, arr) => (
              <React.Fragment key={line.id}>
                <div style={{
                  border: `2px solid ${line.result === "approved" ? "#27ae60" : line.result === "rejected" ? "#e74c3c" : line.result === "skipped" ? "#9aa5ad" : "#d0d7de"}`,
                  borderRadius: 10,
                  padding: "10px 16px",
                  minWidth: 90,
                  textAlign: "center",
                  background: "#fafafa",
                }}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{line.approval_type}</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{line.approver_name}</div>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>{line.position}</div>
                  <div style={{ fontWeight: 700, color: RESULT_COLOR[line.result] }}>
                    {RESULT_LABEL[line.result]}
                  </div>
                  {line.approved_at && (
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                      {line.approved_at.slice(0, 10)}
                    </div>
                  )}
                  {line.comment && (
                    <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 4 }}>
                      💬 {line.comment}
                    </div>
                  )}
                </div>
                {i < arr.length - 1 && (
                  <div style={{ fontSize: 20, color: "#aaa" }}>→</div>
                )}
              </React.Fragment>
            ))}
        </div>
      </div>

      {/* ── 미리보기 영역 ── */}
      <div className={styles.previewWrap} ref={printRef}>
        {doc.document_type === "expense_resolution" ? (
          // ✅ 지출결의/수입결의는 WEHAGO 양식(필드-값 2열 테이블) — 다른 문서종류는 기존 그대로
          <ExpenseResolutionPreview {...buildExpenseResolutionProps(doc)} />
        ) : doc.document_type === "income_resolution" ? (
          <IncomeResolutionPreview {...buildIncomeResolutionProps(doc)} />
        ) : (
          <IncomeProposalPreview
            onSave={null}
            saving={false}
            readOnly={true}
            formData={formData}
            approvalLine={approvalLine}
            onActiveViewChange={(view) => setSearchParams({ view })}
          />
        )}
      </div>

      {/* ── 반려 모달 ── */}
      {showRejectModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={styles.modalTitle}>❌ 반려 사유 입력</h3>
            <textarea
              className={styles.rejectTextarea}
              rows={4}
              placeholder="반려 사유를 입력해주세요."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelBtn}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
              >
                취소
              </button>
              <button
                className={styles.modalRejectBtn}
                onClick={handleReject}
                disabled={actionLoading}
              >
                반려 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}